import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { useOrganizerFollowStatus } from './useOrganizerFollowStatus';
import { useAutoFollow } from './FollowButton/useAutoFollow';

export type FollowNotificationType = 'success' | 'follow-error' | 'unfollow-error';

export interface FollowNotification {
  type: FollowNotificationType;
  organizerName: string;
}

const NOTIFICATION_DISPLAY_TIME = 7000;

interface FollowContextType {
  authenticated: boolean;
  followedByUser: boolean;
  setFollowedByUser: Dispatch<SetStateAction<boolean>>;
  numFollowers: number | undefined;
  setNumFollowers: Dispatch<SetStateAction<number | undefined>>;
  userId: string | undefined;
  statusLoading: boolean;
  mutationLoading: boolean;
  setMutationLoading: Dispatch<SetStateAction<boolean>>;
  shouldShowGDPRModal: boolean;
  setShouldShowGDPRModal: Dispatch<SetStateAction<boolean>>;
  organizerName: string;
  notification: FollowNotification | null;
  showNotification: (type: FollowNotificationType) => void;
  dismissNotification: () => void;
}

const FollowContext = createContext<FollowContextType | undefined>(undefined);

interface FollowProviderProps {
  organizerId: string;
  organizerName: string;
  children: ReactNode;
}

export function FollowProvider({ organizerId, organizerName, children }: FollowProviderProps) {
  const { authenticated, followedByYou, numFollowers, userId, statusLoading } =
    useOrganizerFollowStatus(organizerId);
  const [followedByUser, setFollowedByUser] = useState(followedByYou);
  const [numFollowersState, setNumFollowers] = useState<number | undefined>(numFollowers);
  const [mutationLoading, setMutationLoading] = useState(false);
  const [shouldShowGDPRModal, setShouldShowGDPRModal] = useState(false);
  const [notification, setNotification] = useState<FollowNotification | null>(null);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const dismissNotification = useCallback(() => {
    setNotification(null);
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = undefined;
    }
  }, []);

  const showNotification = useCallback(
    (type: FollowNotificationType) => {
      if (dismissTimerRef.current) {
        clearTimeout(dismissTimerRef.current);
      }
      setNotification({ type, organizerName });
      dismissTimerRef.current = setTimeout(() => {
        setNotification(null);
        dismissTimerRef.current = undefined;
      }, NOTIFICATION_DISPLAY_TIME);
    },
    [organizerName],
  );

  useEffect(() => {
    return () => {
      if (dismissTimerRef.current) {
        clearTimeout(dismissTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setFollowedByUser(followedByYou);
  }, [followedByYou]);

  useEffect(() => {
    setNumFollowers(numFollowers);
  }, [numFollowers]);

  useAutoFollow({
    organizerId,
    authenticated,
    followedByUser: followedByYou,
    statusLoading,
    userId,
    setFollowedByUser,
    setNumFollowers,
    setMutationLoading,
    setShouldShowGDPRModal,
    showNotification,
  });

  const value = useMemo(
    () => ({
      authenticated,
      followedByUser,
      setFollowedByUser,
      numFollowers: numFollowersState,
      setNumFollowers,
      userId,
      statusLoading,
      mutationLoading,
      setMutationLoading,
      shouldShowGDPRModal,
      setShouldShowGDPRModal,
      organizerName,
      notification,
      showNotification,
      dismissNotification,
    }),
    [
      authenticated,
      followedByUser,
      numFollowersState,
      userId,
      statusLoading,
      mutationLoading,
      shouldShowGDPRModal,
      organizerName,
      notification,
      showNotification,
      dismissNotification,
    ],
  );

  return <FollowContext.Provider value={value}>{children}</FollowContext.Provider>;
}

export function useFollowContext() {
  const context = useContext(FollowContext);
  if (!context) {
    throw new Error('useFollow must be used within FollowProvider');
  }
  return context;
}
